import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    History, Loader2, RefreshCw, Package, Calendar, Search, Filter, 
    ChevronDown, ChevronRight, Layers, List, BarChart3, FileText 
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const HistoriqueConsoMP = () => {
    const [historique, setHistorique] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('TOUS');
    const [activeTab, setActiveTab] = useState('HISTORIQUE'); // 'HISTORIQUE' ou 'RAPPORT'
    const [viewMode, setViewMode] = useState('GROUPE'); // 'GROUPE' ou 'LISTE'
    const [expandedGroups, setExpandedGroups] = useState({});
    
    // Filtres par date pour le rapport
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');

    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success',
        message: ''
    });

    const primaryColor = '#b45309';
    const borderColor = '#e2e8f0';

    // --- RÉCUPÉRATION DE L'HISTORIQUE ---
    const fetchHistorique = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get('/verification-magasin/production/historique-consommations');
            setHistorique(res.data || []);
        } catch (err) {
            console.error("Erreur fetch historique consommations:", err);
            showToast("Erreur lors de la récupération de l'historique des consommations.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    // --- GESTION DU TOAST ---
    useEffect(() => {
        if (notification.isOpen) {
            const timer = setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.isOpen]);

    const showToast = (message, type = 'success') => {
        setNotification({ isOpen: true, type, message });
    };

    // --- CHANGEMENT D'ONGLET AVEC RÉINITIALISATION COMPLÈTE DES FILTRES & DATES ---
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchTerm('');
        setFilterType('TOUS');
        setDateDebut('');
        setDateFin('');
    };

    // --- SOCKET.IO ---
    useEffect(() => {
        fetchHistorique();

        if (!socket) return;

        const user = JSON.parse(localStorage.getItem('user'));
        const companyId = user?.company_id || user?.companyId;
        if (companyId) {
            socket.emit('join_company', companyId.toString());
        }

        const handleRefresh = () => {
            fetchHistorique();
        };

        socket.on('REFRESH_UI', handleRefresh);
        socket.on('STOCKS_UPDATED', handleRefresh);

        return () => {
            socket.off('REFRESH_UI', handleRefresh);
            socket.off('STOCKS_UPDATED', handleRefresh);
        };
    }, [fetchHistorique]);

    // --- UTILITAIRE DE CONVERSION DE DATE ROBUSTE (YYYY-MM-DD) ---
    const extractDateOnly = (dateString) => {
        if (!dateString) return '';
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return `${match[1]}-${match[2]}-${match[3]}`;
        }
        const d = new Date(dateString);
        if (!isNaN(d.getTime())) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
        return '';
    };

    // --- FILTRAGE DE L'HISTORIQUE (ONGLET HISTORIQUE) ---
    const historiqueFiltre = useMemo(() => {
        return historique.filter(item => {
            const matchSearch = 
                item.article_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.reference_vente && item.reference_vente.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
                (item.produit_produit_nom && item.produit_produit_nom.toLowerCase().includes(searchTerm.toLowerCase()));
            
            const matchType = filterType === 'TOUS' || item.type === filterType;

            return matchSearch && matchType;
        });
    }, [historique, searchTerm, filterType]);

    // --- REGROUPEMENT PAR ORDRE / BESOIN ---
    const groupesParOrdre = useMemo(() => {
        const groupes = {};
        historiqueFiltre.forEach(item => {
            const ref = item.reference_vente || 'INCONNU';
            if (!groupes[ref]) {
                groupes[ref] = {
                    reference: ref,
                    produitNom: item.produit_produit_nom || 'Production Directe',
                    items: [],
                    dateMax: item.created_at
                };
            }
            groupes[ref].items.push(item);
            if (new Date(item.created_at) > new Date(groupes[ref].dateMax)) {
                groupes[ref].dateMax = item.created_at;
            }
        });
        return Object.values(groupes).sort((a, b) => new Date(b.dateMax) - new Date(a.dateMax));
    }, [historiqueFiltre]);

    // --- FILTRAGE ET REGROUPEMENT PAR MATIÈRE (RAPPORT PÉRIODE) ---
    const rapportFiltre = useMemo(() => {
        return historique.filter(item => {
            const matchType = filterType === 'TOUS' || item.type === filterType;
            const matchSearch = item.article_nom?.toLowerCase().includes(searchTerm.toLowerCase());
            
            if (!item.created_at) return matchType && matchSearch;
            
            const itemDate = extractDateOnly(item.created_at);
            
            if (dateDebut && itemDate < dateDebut) return false;
            if (dateFin && itemDate > dateFin) return false;

            return matchType && matchSearch;
        });
    }, [historique, filterType, searchTerm, dateDebut, dateFin]);

    const rapportGroupedByMaterial = useMemo(() => {
        const map = {};
        rapportFiltre.forEach(item => {
            const key = item.article_nom || 'Inconnu';
            if (!map[key]) {
                map[key] = {
                    article_nom: key,
                    type: item.type,
                    unite: item.unite || '',
                    total_quantite: 0,
                    nombre_utilisations: 0
                };
            }
            map[key].total_quantite += parseFloat(item.quantite) || 0;
            map[key].nombre_utilisations += 1;
        });
        return Object.values(map).sort((a, b) => b.total_quantite - a.total_quantite);
    }, [rapportFiltre]);

    const toggleGroup = (ref) => {
        setExpandedGroups(prev => ({
            ...prev,
            [ref]: !prev[ref]
        }));
    };

    const NotificationToast = () => {
        if (!notification.isOpen) return null;
        const color = notification.type === 'error' ? '#ef4444' : '#10b981';

        return (
            <div style={{
                position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
                zIndex: 3000, display: 'flex', alignItems: 'center',
                background: 'white', padding: '12px 20px', borderRadius: '15px',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', border: `1px solid ${borderColor}`,
                minWidth: '400px', borderLeft: `8px solid ${color}`
            }}>
                <div style={{ flex: 1, fontWeight: '700', color: '#1e293b', fontSize: '13px' }}>
                    {notification.message}
                </div>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', background: '#f1f5f9', height: '100vh', width: '100vw', overflow: 'hidden', position: 'relative' }}>
            <Sidebar />
            <NotificationToast />

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
                <header style={{ padding: '20px 30px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <h2 style={{ color: '#0f172a', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '12px', margin: 0, fontSize: '20px' }}>
                        <History size={28} color={primaryColor} strokeWidth={2.5} /> HISTORIQUE & RAPPORTS DE CONSOMMATIONS
                    </h2>
                    <button onClick={fetchHistorique} style={{ background: '#f8fafc', border: `1px solid ${borderColor}`, padding: '8px 16px', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: '#475569' }}>
                        <RefreshCw size={16} /> Actualiser
                    </button>
                </header>

                <main style={{ flex: 1, padding: '25px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '20px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', gap: '20px' }}>
                        
                        {/* Barre principale de navigation entre Onglets & Filtres de recherche */}
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            
                            {/* Onglets principaux */}
                            <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '12px', border: `1px solid ${borderColor}` }}>
                                <button
                                    onClick={() => handleTabChange('HISTORIQUE')}
                                    style={{
                                        padding: '9px 16px', borderRadius: '9px', fontSize: '13px', fontWeight: '800', cursor: 'pointer',
                                        background: activeTab === 'HISTORIQUE' ? '#fff' : 'transparent',
                                        color: activeTab === 'HISTORIQUE' ? primaryColor : '#64748b',
                                        border: activeTab === 'HISTORIQUE' ? `1px solid ${borderColor}` : 'none',
                                        boxShadow: activeTab === 'HISTORIQUE' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                        display: 'flex', alignItems: 'center', gap: '8px'
                                    }}
                                >
                                    <FileText size={16} /> Historique des Consommations
                                </button>
                                <button
                                    onClick={() => handleTabChange('RAPPORT')}
                                    style={{
                                        padding: '9px 16px', borderRadius: '9px', fontSize: '13px', fontWeight: '800', cursor: 'pointer',
                                        background: activeTab === 'RAPPORT' ? '#fff' : 'transparent',
                                        color: activeTab === 'RAPPORT' ? primaryColor : '#64748b',
                                        border: activeTab === 'RAPPORT' ? `1px solid ${borderColor}` : 'none',
                                        boxShadow: activeTab === 'RAPPORT' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                        display: 'flex', alignItems: 'center', gap: '8px'
                                    }}
                                >
                                    <BarChart3 size={16} /> Rapport par Période (Par Matière)
                                </button>
                            </div>

                            {/* Recherche et Type */}
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <div style={{ position: 'relative', width: '260px' }}>
                                    <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                                    <input 
                                        type="text" 
                                        placeholder="Filtrer par article..." 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{
                                            width: '100%', padding: '9px 14px 9px 42px', borderRadius: '10px',
                                            border: `1px solid ${borderColor}`, outline: 'none', fontSize: '13px',
                                            background: '#f8fafc', fontWeight: '600', color: '#1e293b'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                    <Filter size={16} color="#64748b" />
                                    {['TOUS', 'MP', 'PSF'].map((type) => (
                                        <button
                                            key={type}
                                            onClick={() => setFilterType(type)}
                                            style={{
                                                padding: '8px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '800', cursor: 'pointer',
                                                background: filterType === type ? '#334155' : '#f8fafc',
                                                color: filterType === type ? '#fff' : '#64748b',
                                                border: `1px solid ${filterType === type ? '#334155' : borderColor}`
                                            }}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Si nous sommes sur l'onglet RAPPORT par période, afficher les sélecteurs de date */}
                        {activeTab === 'RAPPORT' && (
                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', background: '#f8fafc', padding: '12px 16px', borderRadius: '12px', border: `1px solid ${borderColor}`, flexShrink: 0 }}>
                                <span style={{ fontSize: '13px', fontWeight: '800', color: '#334155' }}>Période du :</span>
                                <input 
                                    type="date" 
                                    value={dateDebut} 
                                    onChange={(e) => setDateDebut(e.target.value)}
                                    style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${borderColor}`, fontSize: '13px', fontWeight: '600', background: '#fff' }}
                                />
                                <span style={{ fontSize: '13px', fontWeight: '800', color: '#334155' }}>au</span>
                                <input 
                                    type="date" 
                                    value={dateFin} 
                                    onChange={(e) => setDateFin(e.target.value)}
                                    style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${borderColor}`, fontSize: '13px', fontWeight: '600', background: '#fff' }}
                                />
                                {(dateDebut || dateFin) && (
                                    <button 
                                        onClick={() => { setDateDebut(''); setDateFin(''); }} 
                                        style={{ background: 'transparent', border: 'none', color: '#ef4444', fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}
                                    >
                                        Réinitialiser les dates
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Si nous sommes sur l'onglet HISTORIQUE, afficher le sélecteur de vue (Groupé / Liste) */}
                        {activeTab === 'HISTORIQUE' && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                                <div style={{ display: 'flex', background: '#f1f5f9', padding: '3px', borderRadius: '10px', border: `1px solid ${borderColor}` }}>
                                    <button
                                        onClick={() => setViewMode('GROUPE')}
                                        style={{
                                            padding: '7px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '800', cursor: 'pointer',
                                            background: viewMode === 'GROUPE' ? primaryColor : 'transparent',
                                            color: viewMode === 'GROUPE' ? '#fff' : '#64748b',
                                            border: 'none', display: 'flex', alignItems: 'center', gap: '6px'
                                        }}
                                    >
                                        <Layers size={14} /> Par Ordre (Groupé)
                                    </button>
                                    <button
                                        onClick={() => setViewMode('LISTE')}
                                        style={{
                                            padding: '7px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '800', cursor: 'pointer',
                                            background: viewMode === 'LISTE' ? primaryColor : 'transparent',
                                            color: viewMode === 'LISTE' ? '#fff' : '#64748b',
                                            border: 'none', display: 'flex', alignItems: 'center', gap: '6px'
                                        }}
                                    >
                                        <List size={14} /> Tous (Liste)
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* CONTENU PRINCIPAL AVEC SCROLL INTERNE */}
                        <div style={{ flex: 1, overflowY: 'auto', border: `1px solid ${borderColor}`, borderRadius: '12px', background: '#fff' }}>
                            {loading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
                                    <Loader2 className="animate-spin" color={primaryColor} size={40} />
                                </div>
                            ) : activeTab === 'HISTORIQUE' ? (
                                // --- ONGLET HISTORIQUE ---
                                historiqueFiltre.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '60px', color: '#94a3b8' }}>
                                        <Package size={64} strokeWidth={1.5} />
                                        <p style={{ fontWeight: '700', marginTop: '15px', fontSize: '16px' }}>Aucun historique trouvé</p>
                                        <p style={{ fontSize: '13px', margin: '5px 0 0 0' }}>Modifiez vos filtres ou effectuez de nouvelles consommations.</p>
                                    </div>
                                ) : viewMode === 'GROUPE' ? (
                                    // VUE GROUPÉE PAR ORDRE
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px' }}>
                                        {groupesParOrdre.map(groupe => {
                                            const isExpanded = !!expandedGroups[groupe.reference];
                                            const shortRef = groupe.reference !== 'N/A' && groupe.reference.length > 8 ? groupe.reference.toString().slice(-8) : groupe.reference;
                                            
                                            return (
                                                <div key={groupe.reference} style={{ border: `1px solid ${borderColor}`, borderRadius: '10px', overflow: 'hidden', background: '#fff' }}>
                                                    <div 
                                                        onClick={() => toggleGroup(groupe.reference)}
                                                        style={{
                                                            padding: '12px 16px', background: isExpanded ? '#f8fafc' : '#fff', cursor: 'pointer',
                                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.2s'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                            <div style={{ background: '#fef3c7', color: primaryColor, padding: '6px', borderRadius: '6px' }}>
                                                                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                            </div>
                                                            <div>
                                                                <span style={{ fontWeight: '900', color: '#1e293b', fontSize: '13.5px' }}>
                                                                    Ordre / Besoin : #{shortRef} — Produit : {groupe.produitNom}
                                                                </span>
                                                                <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '1px' }}>
                                                                    {groupe.items.length} consommation(s)
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: '600' }}>
                                                            {new Date(groupe.dateMax).toLocaleString()}
                                                        </span>
                                                    </div>

                                                    {isExpanded && (
                                                        <div style={{ borderTop: `1px solid ${borderColor}`, background: '#fcfcfc', padding: '10px 16px' }}>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                                <thead>
                                                                    <tr style={{ textAlign: 'left', fontSize: '11px', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                                                                        <th style={{ padding: '8px' }}>DATE / HEURE</th>
                                                                        <th style={{ padding: '8px' }}>ARTICLE</th>
                                                                        <th style={{ padding: '8px' }}>TYPE</th>
                                                                        <th style={{ padding: '8px' }}>QTÉ CONSOMMÉE</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {groupe.items.map(item => (
                                                                        <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '12px' }}>
                                                                            <td style={{ padding: '8px', color: '#64748b', fontWeight: '600' }}>
                                                                                {new Date(item.created_at).toLocaleString()}
                                                                            </td>
                                                                            <td style={{ padding: '8px', fontWeight: '700', color: '#1e293b' }}>
                                                                                {item.article_nom}
                                                                            </td>
                                                                            <td style={{ padding: '8px' }}>
                                                                                <span style={{ fontWeight: '800', color: item.type === 'MP' ? '#2563eb' : '#7c3aed', background: item.type === 'MP' ? '#eff6ff' : '#f5f3ff', padding: '2px 8px', borderRadius: '20px', fontSize: '10px' }}>
                                                                                    {item.type}
                                                                                </span>
                                                                            </td>
                                                                            <td style={{ padding: '8px', fontWeight: '900', color: '#dc2626' }}>
                                                                                -{item.quantite} {item.unite || ''}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    // VUE LISTE TOUS
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ textAlign: 'left', fontSize: '12px', color: '#64748b', borderBottom: '2px solid #f1f5f9', background: '#f8fafc', position: 'sticky', top: 0, zIndex: 10 }}>
                                                <th style={{ padding: '14px 15px' }}>DATE / HEURE</th>
                                                <th style={{ padding: '14px 15px' }}>ARTICLE PRODUIT</th>
                                                <th style={{ padding: '14px 15px' }}>MATIÈRE CONSUMÉE (MP / PSF)</th>
                                                <th style={{ padding: '14px 15px' }}>TYPE</th>
                                                <th style={{ padding: '14px 15px' }}>QTÉ CONSOMMÉE</th>
                                                <th style={{ padding: '14px 15px' }}>RÉFÉRENCE</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historiqueFiltre.map(item => (
                                                <tr key={item.id} style={{ borderBottom: '1px solid #f8fafc', fontSize: '13px' }}>
                                                    <td style={{ padding: '12px 15px', color: '#64748b', fontWeight: '700' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <Calendar size={14} /> {new Date(item.created_at).toLocaleString()}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px 15px', fontWeight: '800', color: primaryColor }}>
                                                        {item.produit_produit_nom || 'Production Directe'}
                                                    </td>
                                                    <td style={{ padding: '12px 15px', fontWeight: '800', color: '#1e293b' }}>
                                                        {item.article_nom}
                                                    </td>
                                                    <td style={{ padding: '12px 15px' }}>
                                                        <span style={{ fontWeight: '800', color: item.type === 'MP' ? '#2563eb' : '#7c3aed', background: item.type === 'MP' ? '#eff6ff' : '#f5f3ff', padding: '4px 10px', borderRadius: '20px', fontSize: '11px' }}>
                                                            {item.type}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 15px', fontWeight: '900', color: '#dc2626' }}>
                                                        -{item.quantite} {item.unite || ''}
                                                    </td>
                                                    <td style={{ padding: '12px 15px', color: '#475569' }}>
                                                        #{item.reference_vente ? item.reference_vente.toString().slice(-8) : 'N/A'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            ) : (
                                // --- ONGLET RAPPORT PAR PÉRIODE (REGROUPÉ PAR DÉSIGNATION DE MATIÈRE) ---
                                rapportGroupedByMaterial.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '60px', color: '#94a3b8' }}>
                                        <Package size={64} strokeWidth={1.5} />
                                        <p style={{ fontWeight: '700', marginTop: '15px', fontSize: '16px' }}>Aucune consommation trouvée pour cette période</p>
                                        <p style={{ fontSize: '13px', margin: '5px 0 0 0' }}>Modifiez vos dates ou filtres de recherche.</p>
                                    </div>
                                ) : (
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ textAlign: 'left', fontSize: '12px', color: '#64748b', borderBottom: '2px solid #f1f5f9', background: '#f8fafc', position: 'sticky', top: 0, zIndex: 10 }}>
                                                <th style={{ padding: '14px 15px' }}>DÉSIGNATION DE LA MATIÈRE / ARTICLE</th>
                                                <th style={{ padding: '14px 15px' }}>TYPE</th>
                                                <th style={{ padding: '14px 15px' }}>NOMBRE DE SORTIES</th>
                                                <th style={{ padding: '14px 15px' }}>TOTAL QUANTITÉ CONSOMMÉE</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rapportGroupedByMaterial.map((row, idx) => (
                                                <tr key={idx} style={{ borderBottom: '1px solid #f8fafc', fontSize: '13px' }}>
                                                    <td style={{ padding: '12px 15px', fontWeight: '800', color: '#1e293b' }}>
                                                        {row.article_nom}
                                                    </td>
                                                    <td style={{ padding: '12px 15px' }}>
                                                        <span style={{ fontWeight: '800', color: row.type === 'MP' ? '#2563eb' : '#7c3aed', background: row.type === 'MP' ? '#eff6ff' : '#f5f3ff', padding: '4px 10px', borderRadius: '20px', fontSize: '11px' }}>
                                                            {row.type}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 15px', fontWeight: '700', color: '#475569' }}>
                                                        {row.nombre_utilisations} fois
                                                    </td>
                                                    <td style={{ padding: '12px 15px', fontWeight: '900', color: '#dc2626' }}>
                                                        -{row.total_quantite.toFixed(3)} {row.unite}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}
                        </div>

                    </div>
                </main>
            </div>
        </div>
    );
};

export default HistoriqueConsoMP;